import { memo, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { isNil, isEmpty, noop } from 'lodash';
import { CJPartnerBonusIdValidationSchema } from 'validators/schemas.validation';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import OptionList from 'components/common/OptionList';
// Import Services
import { BonusHttpService } from 'services/http';
import { Helpers } from 'services';
import { getCustomerJourneyFormFieldTranslatableErrorText } from 'services/customerJourney/helpers';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useAutocompleteRequest from 'hooks/UseAutocompleteRequest';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Constants
import { l, BonusTypesLabels, CustomerJourneyErrorCodes, DEFAULT_SORTING_DIRECTION } from 'constants/common';

const { customMomentWithoutTimezoneConversion } = Helpers;

const CJPartnerBonusId = ({
    defaultValue,
    getUpdate,
    option,
    updateDependency,
    setBonusDetails,
    isWFBonus = false,
}) => {
    const { t } = useTranslation();
    const { doPostRequest, doGetRequest } = useRequest();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();
    const { CustomAttributes } = option;
    const { SupportedBonusTypes = [] } = CustomAttributes;
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [listData, setListData] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const [selectedBonusData, setSelectedBonusData] = useState({
        Id: defaultValue?.value,
        Name: !isNil(defaultValue?.value) ? `${defaultValue.value}` : null,
    });
    const [isVisiblePopover, setIsVisiblePopover] = useState(false);
    const [isBonusByIdRequestFailed, setIsBonusByIdRequestFailed] = useState(false);
    const [isBonusByIdInitFailed, setIsBonusByIdInitFailed] = useState(false);

    const { getAutocompleteRequest: getAutocompleteBonusListRequest, cancelRequest: cancelGetBonusList } =
        useAutocompleteRequest();
    const { getAutocompleteRequest: getAutocompleteBonusByIdRequest, cancelRequest: cancelGetBonusById } =
        useAutocompleteRequest();

    const { getBonusList, getBonusById } = useRef({
        getBonusList: BonusHttpService.getBonusList,
        getBonusById: BonusHttpService.getBonusById,
    }).current;

    const formik = useFormik({
        initialValues: {
            value: defaultValue?.value,
            triggerType: defaultValue?.triggerType,
        } as AnyObject,
        onSubmit: noop,
        validationSchema: CJPartnerBonusIdValidationSchema(option),
    });
    const { values, setFieldValue, setValues, errors, setFieldError, submitForm } = formik;
    const { value, triggerType } = values;

    useEffect(() => {
        getUpdate({ value, triggerType });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    // TODO: config
    const bonusListGetterMethod = (searchValue = '') => {
        setIsLoadingList(true);
        const getSearchValueType = (value) => {
            return /^( )*\d+( )*$/.test(value) ? 'Id' : 'Name';
        };

        const requestObj = getAutocompleteBonusListRequest(getBonusList);

        // TODO: Remove this filtering after CJ service release from backend
        const supportedBonusTypesWithoutFreeChip = isWFBonus
            ? SupportedBonusTypes
            : SupportedBonusTypes.filter((type) => type !== BonusTypesLabels.FreeChip);

        doPostRequest(requestObj.request, {
            requestBody: {
                Filters: [
                    { Name: 'TargetObject', Comparision: 0, Values: [0] },
                    { Name: getSearchValueType(searchValue), Comparision: 2, Values: [searchValue] },
                    { Name: 'Type', Comparision: 8, Values: [...supportedBonusTypesWithoutFreeChip] },
                ],
                Pageing: { PageSize: 100, PageNumber: 1 },
                Sorting: { Name: 'StartDate', Direction: DEFAULT_SORTING_DIRECTION },
            },
            successCallback: (data) => {
                setListData(
                    !isNil(data?.Data)
                        ? data.Data?.map((item) => ({
                              ...item,
                              formattedStartDate: customMomentWithoutTimezoneConversion(item.StartDate).format(
                                  partnerDateTimeWithoutSecondsFormat,
                              ),
                              formattedEndDate: customMomentWithoutTimezoneConversion(item.EndDate).format(
                                  partnerDateTimeWithoutSecondsFormat,
                              ),
                              bonusType: BonusTypesLabels[item.Type],
                          }))
                        : [],
                );
                setIsLoadingList(false);
            },
            errorCallback: () => setIsLoadingList(false),
        });
    };

    const searchBonusHandler = ({ target }) => {
        setSearchValue(target.value);
        bonusListGetterMethod(target.value);
    };

    const init = ({ shouldRestoreSavedDataWhenBonusDetailsReloaded }: AnyObject = {}) => {
        submitForm();
        bonusListGetterMethod('');

        if (!isNil(defaultValue?.value) && !isEmpty(defaultValue.value.toString())) {
            setIsLoading(true);
            const getBonusByIdRequest = getAutocompleteBonusByIdRequest(getBonusById);
            setBonusDetails(
                new Promise((resolve, reject) => {
                    doGetRequest(getBonusByIdRequest.request, {
                        queryString: { bonusId: defaultValue.value },
                        successCallback: (responseData) => {
                            setIsBonusByIdRequestFailed(false);
                            updateDependency({ ...responseData, dependencyItemId: responseData.Id });
                            setSelectedBonusData(responseData);
                            setIsLoading(false);
                            resolve(responseData);
                        },
                        errorCallback: () => {
                            setIsBonusByIdRequestFailed(true);
                            setIsBonusByIdInitFailed(true);
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            setFieldError('value', CustomerJourneyErrorCodes.bonusDetailsGettingError);
                            setIsLoading(false);
                            reject();
                        },
                    });
                }),
                shouldRestoreSavedDataWhenBonusDetailsReloaded,
            );
        }
    };

    const cleanUp = () => {
        return () => {
            cancelGetBonusById('CJPartnerBonusId:getBonusById');
            cancelGetBonusList('CJPartnerBonusId:getBonusList');
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const popoverToggler = () => {
        setIsVisiblePopover(!isVisiblePopover);
    };

    const selectBonusHandler = (selectionData) => {
        const { shouldRestoreSavedDataWhenBonusDetailsReloaded, ...data } = selectionData;
        const selectedOldBonusId = selectedBonusData.Id;
        setIsLoading(true);
        setSelectedBonusData(data);

        const getBonusByIdRequest = getAutocompleteBonusByIdRequest(getBonusById);

        setBonusDetails(
            new Promise((resolve, reject) => {
                doGetRequest(getBonusByIdRequest.request, {
                    queryString: { bonusId: data.Id },
                    successCallback: (responseData) => {
                        setIsBonusByIdRequestFailed(false);
                        setValues({ value: responseData?.Id, triggerType: responseData?.TriggerType });
                        updateDependency({ ...responseData, dependencyItemId: responseData?.Id });
                        setIsLoading(false);
                        resolve(responseData);
                    },
                    errorCallback: () => {
                        setIsBonusByIdInitFailed(false);
                        setIsBonusByIdRequestFailed(true);

                        if (!isNil(selectedOldBonusId)) {
                            updateDependency({
                                dependencyItemId: selectedOldBonusId,
                                shouldRemoveDependency: true,
                            });
                        }

                        setFieldValue('value', '', false);
                        setFieldValue('triggerType', undefined, false);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        setFieldError('value', CustomerJourneyErrorCodes.bonusDetailsGettingError);
                        setIsLoading(false);
                        reject();
                    },
                });
            }),
            shouldRestoreSavedDataWhenBonusDetailsReloaded,
        );

        setIsVisiblePopover(false);
    };

    const handleReloadBonusData = () => {
        if (isBonusByIdInitFailed) {
            init({ shouldRestoreSavedDataWhenBonusDetailsReloaded: true });
        } else {
            selectBonusHandler({ ...selectedBonusData, shouldRestoreSavedDataWhenBonusDetailsReloaded: true });
        }
    };

    return (
        <>
            <OptionList
                optionLabel={selectedBonusData?.Name}
                toggleHandler={popoverToggler}
                isVisible={isVisiblePopover}
                setIsVisible={setIsVisiblePopover}
                keys={['Name', 'formattedStartDate', 'formattedEndDate', 'bonusType', 'Id']}
                titles={[t(l.BonusName), t(l.StartDate), t(l.EndDate), t(l.Type), t(l.ID)]}
                selectedPropertyKey="Id"
                list={listData}
                rowCount={listData.length}
                searchHandler={searchBonusHandler}
                optionValue={value}
                optionSelectHandler={selectBonusHandler}
                contentTop={45}
                contentWrapperClassName="cj-option-list-content-wrapper"
                searchValue={searchValue}
                isLoading={isLoading}
                isLoadingList={isLoadingList}
                isValid={!errors.value}
                errorText={getCustomerJourneyFormFieldTranslatableErrorText(t, errors?.value)}
            />
            {isBonusByIdRequestFailed && (
                <div>
                    <ButtonWithLoader
                        isLoading={isLoading}
                        color="primary"
                        defaultClassNames={classNames('crm-modal-reload-action-btn', {
                            'crm-modal-reload-action-loading': isLoading,
                        })}
                        onClick={handleReloadBonusData}
                    >
                        {t(l.ReloadData)}
                    </ButtonWithLoader>
                </div>
            )}
        </>
    );
};

CJPartnerBonusId.propTypes = {
    defaultValue: PropTypes.object,
    getUpdate: PropTypes.func.isRequired,
    option: PropTypes.object.isRequired,
    updateDependency: PropTypes.func.isRequired,
    setBonusDetails: PropTypes.func,
    isWFBonus: PropTypes.bool,
};

CJPartnerBonusId.defaultProps = {
    defaultValue: { value: '' },
    setBonusDetails: noop,
};

export default memo(CJPartnerBonusId);
